import { AccountSummary, OnlineDocs } from '@/definitions/definitions';
import { endpoints } from '@/endpoints/endpoints';
import { handleError } from '@/utils/helperFunctions';
import axios from 'axios';
import { Module } from 'vuex';
import { validate, formatValidationMessages } from '@/validation';
import { AnySchema } from '@hapi/joi';

// use only in development
// axios.defaults.headers.common["ngrok-skip-browser-warning"] = true

export const onlineDocs: Module<OnlineDocs, any> = {
  state: {
    open: false,
    selectedDocument: null,
    clients: [],
    clientFeeSchedule: null,
    currencies: [],
    isLoading: false,
    isFetching: false,
    docTitle: '',
    address: [
      {
        firstholder: {
          UserInfoID: null,
        },
      },
    ],
    previewPDF: null,
    onlineFormData: null,
    documentCode: null,
    currentStep: 1,
    clientSummary: null,
    maxDisbursementAmount: 0,
    availableCashInEUR: 0,
  },
  mutations: {
    toggleDocumentForm(state: OnlineDocs, data: boolean) {
      state.open = data;
    },
    toggleLoading(state: OnlineDocs, data: boolean) {
      state.isLoading = data;
    },
    toggleFetching(state: OnlineDocs, data: boolean) {
      state.isFetching = data;
    },
    setCurrentStep(state: OnlineDocs, data: number | null) {
      state.currentStep = data;
    },
    setSelectedDocument(state: OnlineDocs, data: string | null) {
      state.selectedDocument = data;
    },
    setDocumentTitle(state: OnlineDocs, data: string) {
      state.docTitle = data;
    },
    setClients(state: OnlineDocs, data) {
      state.clients = data;
    },
    setClientFeeAndCharge(state: OnlineDocs, data) {
      state.clientFeeSchedule = data;
    },
    setClientAddress(state: OnlineDocs, data) {
      state.address = data;
    },
    setClientCurrencies(state: OnlineDocs, data) {
      state.currencies = data;
    },
    setOnlineFormData(state: OnlineDocs, data) {
      state.onlineFormData = data;
    },
    setDocumentCode(state: OnlineDocs, data) {
      state.documentCode = data;
    },
    setPreviewData(state: OnlineDocs, data) {
      state.previewPDF = data;
    },
    setClientSummary(state: OnlineDocs, data) {
      state.clientSummary = data;
    },
    setMaxAmount(state: OnlineDocs, data: number) {
      state.maxDisbursementAmount = data;
    },
    setAvailableCash(state: OnlineDocs, data: number) {
      state.availableCashInEUR = data;
    },
  },
  actions: {
    toggleDcoumentForm(state, data: boolean) {
      this.commit('toggleDocumentForm', data);
      if (!data) this.commit('setSelectedDocument');
    },
    setSelectedDocument(state, data: string | null) {
      this.commit('setSelectedDocument', data);
    },
    setCurrentStep(state, data: number | null) {
      this.commit('setCurrentStep', data);
    },
    setDocumentTitle(state, data: string | null) {
      this.commit('setDocumentTitle', data);
    },
    setOnlineFormData(state, data: any | null) {
      this.commit('setOnlineFormData', data);
    },
    setDocumentCode(state, data: string | null) {
      this.commit('setDocumentCode', data);
    },
    setPreviewData(state, data: string | null) {
      this.commit('setPreviewData', data);
    },
    setMaxAmount(state, data: number) {
      this.commit('setMaxAmount', data);
    },
    setAvailableCash(state, data: number) {
      this.commit('setAvailableCash', data);
    },
    getClients(state, isTrusteeFiltered: boolean) {
      let url = '';
      if (isTrusteeFiltered) {
        url = '&required_trustee=true';
      }
      axios
        .get(
          `${endpoints.clients}?whitelabelID=${parseInt(
            this.state.account.details.whiteLabelId,
            10,
          )}${url}`,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then(({ data }) => {
          this.commit('setClients', data.data);
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Something wrong fetching clients.',
          );
          this.dispatch('addError', msg);
        });
    },
    getClientFeeSchedule(state, data: any) {
      axios
        .get(
          `${endpoints.clientFeeSchedule}/${
            data.client
          }?whitelabelID=${parseInt(
            this.state.account.details.whiteLabelId,
            10,
          )}&feeScheduleID=${data.feeId}`,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then(({ data: dataResponse }) => {
          const schedule: any = [];
          // data.data.map(({Type_Name, Value, FeeTypeID}: {Type_Name: string, Value: string, FeeTypeID: string}) => schedule[Type_Name.replace(/\s/g,'')] = Value)
          this.commit('setClientFeeAndCharge', dataResponse.data);
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something wrong while fetching fee schedule for the client.',
          );
          this.dispatch('addError', msg);
        });
    },

    getClientAddress() {
      axios
        .get(
          `${endpoints.getClientAddress}?whitelabelID=${parseInt(
            this.state.account.details.whiteLabelId,
            10,
          )}`,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then(({ data }) => {
          if (data.data.length > 0) {
            this.commit('setClientAddress', data.data);
          }
        })
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Something wrong fetching address.',
          );
          this.dispatch('addError', msg);
        });
    },

    getClientCurrencies({ commit }) {
      axios
        .get(
          `${endpoints.cashPosition}?whitelabelID=${parseInt(
            this.state.account.details.whiteLabelId,
            10,
          )}`,
          {
            headers: {
              Authorization: 'bearer ' + this.state.account.token,
            },
          },
        )
        .then(({ data }) => commit('setClientCurrencies', data.data))
        .catch((err) => {
          const msg = handleError(
            err,
            'Sorry! Looks like something went wrong while loading your available cash position',
          );
          this.dispatch('addError', msg);
        });
    },
    async getClientSummary({ commit, dispatch }, platformId: number) {
      commit('toggleFetching', true);
      try {
        const { data } = await axios.get(
          `${endpoints.accountSummary}${platformId}`,
          {
            headers: {
              Authorization: `bearer ${this.state.account.token}`,
            },
          },
        );
        commit('setClientSummary', data.Data[0]);
      } catch (err) {
        const msg = handleError(
          err,
          "Sorry! Looks like something went wrong while loading the user's account summary",
        );
        dispatch('addError', msg);
      } finally {
        commit('toggleFetching', false);
      }
    },
    generateDocuSignPdf(
      state,
      data: { formData: any; joiSchema: AnySchema; documentCode: string },
    ) {
      const validation = validate(
        data.formData.data ? data.formData.data : data.formData,
        data.joiSchema,
      );

      if (validation.error) {
        const errArr: string[] = formatValidationMessages(
          validation.error.details,
        );
        this.dispatch('addError', errArr[0]);
      } else {
        let postData;
        const previewData = data.formData.preview;
        // change data format for change of address
        if ('country_1' in data.formData?.data) {
          postData = {
            firstholder: {
              UserInfoID: data.formData.firstholder.UserInfoID,
            },
            data: {
              checkbox_account_holder_1:
                data.formData.data.checkbox_account_holder_1,
              checkbox_account_holder_3:
                data.formData.data.checkbox_account_holder_3,
              CheckBox_address_1: data.formData.data.CheckBox_address_1,
              CheckBox_address_2: data.formData.data.CheckBox_address_2,
              address_street_1: `${data.formData.data.street_1} ${data.formData.data.no_1}`,
              address_pc_city_1: `${data.formData.data.postcode_1}, ${data.formData.data.place_1}`,
              address_country_1: data.formData.data.country_1,
              address_street_2: '',
              address_pc_city_2: '',
              address_country_2: '',
            },
            preview: data.formData.preview,
          };
          if (data.formData.data.different_address) {
            postData.data.address_street_2 = `${data.formData.data.street_2} ${data.formData.data.no_2}`;
            postData.data.address_pc_city_2 = `${data.formData.data.postcode_2}, ${data.formData.data.place_2}`;
            postData.data.address_country_2 = data.formData.data.country_2;
          }
        } else {
          postData = data.formData;
        }
        this.commit('toggleLoading', true);
        // axios.post()
        axios
          .post(
            `${endpoints.generateDocument}/${data.documentCode}`,
            postData,
            {
              headers: {
                Authorization: 'bearer ' + this.state.account.token,
              },
              responseType: previewData === true ? 'blob' : 'json',
            },
          )
          .then(({ status, data: dataResponse }) => {
            this.commit('toggleLoading', false);
            if (previewData === true) {
              if (status === 200) {
                this.commit('setPreviewData', dataResponse);
                this.commit('setSelectedDocument', 'PDFPreview');
              }
            } else {
              if (status === 200) this.commit('setSelectedDocument', 'success');
            }
          })
          .catch((err) => {
            const msg = handleError(
              err,
              'Sorry! Something wrong generating document.' + err,
            );
            this.dispatch('addError', msg);
            this.commit('toggleLoading', false);
            this.commit('toggleDocumentForm', false);
          });
      }
    },
  },
};
